import { TextField } from "@mui/material";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

interface VoucherInputFieldProps {
  onVoucherCodeChange: (voucherCode: string) => void;
  errorMessage?: any;
  voucherCode?: string | undefined;
}

export default function VoucherInputField({
  onVoucherCodeChange,
  errorMessage,
  voucherCode: voucherCodeFromContext,
}: VoucherInputFieldProps) {
  const { t } = useTranslation();

  const [voucherCode, setVoucherCode] = useState(voucherCodeFromContext || "");
  const [error, setError] = useState(errorMessage.length > 0 ? true : false);
  const [localErrorMessage, setLocalErrorMessage] = useState("");

  const voucherCodeRegex = /^[A-Z]{4}-[0-9]{4}-[A-Z]{4}$/;

  const formatVoucherCode = (value: string) => {
    const cleanedValue = value.replace(/[^A-Z0-9-]/gi, "").toUpperCase();

    if (cleanedValue.length === 4 && !cleanedValue.includes("-")) {
      return cleanedValue + "-";
    } else if (
      cleanedValue.length === 9 &&
      cleanedValue.indexOf("-") === 4 &&
      cleanedValue.lastIndexOf("-") === 4
    ) {
      return cleanedValue + "-";
    }

    return cleanedValue;
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    const isDeleting = voucherCode.length > value.length;

    if (
      isDeleting &&
      voucherCode.endsWith("-") &&
      value === voucherCode.slice(0, -1)
    ) {
      const newValue = voucherCode.slice(0, -2);
      setVoucherCode(newValue);
      onVoucherCodeChange(newValue);
      return;
    }

    const formattedValue = formatVoucherCode(value);

    if (voucherCodeRegex.test(formattedValue)) {
      setError(false);
      setLocalErrorMessage("");

      setVoucherCode(formattedValue);
      onVoucherCodeChange(formattedValue);
    } else {
      setError(true);
      setLocalErrorMessage(
        t("AddOrUseVoucher.Voucher.UseVoucherTab.errorMessage.generic")
      );

      setVoucherCode(formattedValue);
      onVoucherCodeChange(formattedValue);
    }
  };

  useEffect(() => {
    if (errorMessage) {
      setError(true);
      setLocalErrorMessage(errorMessage);
    }
  }, [errorMessage]);

  return (
    <TextField
      placeholder={t("BuyVoucherPrompt.applyVoucherCode.placeholder")}
      variant="outlined"
      fullWidth
      value={voucherCode}
      onChange={handleInputChange}
      error={error}
      helperText={error && localErrorMessage}
      slotProps={{
        input: {
          sx: {
            "&::placeholder": {
              fontSize: 2,
              color: "neutral.light",
            },
          },
          "aria-label": t(
            "AddOrUseVoucher.Voucher.UseVoucherTab.codeInput.label"
          ),
        },
      }}
      sx={{
        borderColor: "neutral.light",
        borderRadius: "5px",
        fontSize: "12px",
      }}
    />
  );
}
